<template>
  <div class="policy-container">
    <div class="main-title">{{ appName }} Service Terms</div>

    <div class="intro-block">
      Please carefully read the following service terms before you register and log into our platform. This document defines the rights and responsibilities between the platform and all users, and protects the legitimate interests of every party. Please pay close attention to all important statements in this agreement.

      By completing account registration, signing in or using any of our functions, you confirm that you have fully read, understood and agreed to abide by all terms hereof. We reserve the right to modify, update or adjust this agreement, privacy policy and related rules at any time without prior notice. Your continued use of the service after updates will be deemed acceptance of the revised terms. If you do not agree with any revised content, please stop using our services immediately.
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">1. User Account Rules</h4>
      <div class="item-content">
        1.1 To use our services, you need to create a user account. We may adjust the account registration process according to actual operation.
        <br /><br />
        1.2 After you register an account, you agree that we may activate accounts for other products under our company at the same time.
        <br /><br />
        1.3 All accounts belong to the platform. Users only obtain the right to use them. It is forbidden to gift, lend, rent, transfer or sell accounts to others.
        <br /><br />
        1.4 You shall take full responsibility for the safety of your account and password. Any loss caused by your carelessness shall be borne by yourself. All operations under your account will be regarded as your personal behaviors. Please inform us promptly once you find abnormal login or unauthorized use. You shall not use other people’s accounts or passwords in any situation.
        <br /><br />
        1.5 If you violate any rules in this agreement, we have the right to restrict or suspend your service access.
        <br /><br />
        1.6 If your account has not been logged in for one consecutive year, we have the right to recycle or delete it without notice. We also retain the right to recover accounts and usernames for operational reasons.
        <br /><br />
      </div>
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">2. Service Range</h4>
      <div class="item-content">
        2.1 Our software provides video chat, in-app purchase, virtual gift sending and other functions. We can adjust or change service contents at any time without prior notice.
        <br /><br />
        2.2 You agree that we may send commercial advertisements and service reminders through email, app push, SMS and web pages. Meanwhile, we may collect basic information of your mobile device to ensure normal operation.
        <br /><br />
      </div>
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">3. Personal Data Protection</h4>
      <div class="item-content">
        3.1 When registering or using services, you need to provide real and valid identity information as required by laws. Incomplete or false information may result in restrictions on your use.
        <br /><br />
        3.2 We may collect and use your personal information to improve service quality, and may share data with authorized third parties. For details, please check our Privacy Policy.
      </div>
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">4. Content Release Specifications</h4>
      <div class="item-content">
        4.1 The content you publish includes avatars, nicknames, profiles, texts, audios, images, videos, messages, links and all data generated during use.
        <br /><br />
        4.2 You must not publish or spread the following content, nor engage in the following behaviors:
        <br /><br />
        1. Content that endangers national security, state secrets or national unity;
        <br /><br />
        2. Content that damages national honor and interests;
        <br /><br />
        3. Content that incites ethnic hatred or ethnic division;
        <br /><br />
        4. Content that violates religious policies or promotes cults and feudal superstition;
        <br /><br />
        5. Content that spreads rumors and disturbs public order;
        <br /><br />
        6. Pornographic, vulgar and indecent content;
        <br /><br />
        7. Content that advocates gambling, violence, murder and terrorism;
        <br /><br />
        8. Insult, slander, fraud and content that infringes others’ legal rights;
        <br /><br />
        9. Content containing abusive, threatening words;
        <br /><br />
        10. Sending harassment information, spam, malicious programs and phishing links;
        <br /><br />
        11. Disclosing other people’s privacy and personal information;
        <br /><br />
        12. Infringing others’ reputation, portrait right, intellectual property and trade secrets;
        <br /><br />
        13. Content that harms the legitimate rights and interests of minors;
        <br /><br />
        14. Stalking and harassing other users;
        <br /><br />
        15. Using false information or impersonating others;
        <br /><br />
        16. Inducing or forcing others to follow accounts or click links;
        <br /><br />
        17. Fabricating facts to mislead others;
        <br /><br />
        18. Posting commercial promotion information without permission;
        <br /><br />
        19. Using technical means to register a large number of fake accounts;
        <br /><br />
        20. Deleting the copyright information of this software;
        <br /><br />
        21. Carrying out reverse analysis and cracking the software source code;
        <br /><br />
        22. Copying, modifying and building mirror sites involving our intellectual property;
        <br /><br />
        23. Tampering with platform data and making derivative works;
        <br /><br />
        24. Modifying software functions and spreading modified programs;
        <br /><br />
        25. Using unauthorized plug-ins, scripts and tools to log in;
        <br /><br />
        26. Publishing offensive and inappropriate content;
        <br /><br />
        27. Other behaviors that violate laws and regulations, damage third-party rights or affect normal operation. We will review published content within 24 hours. Violating content will be deleted, and serious violations will lead to account ban.
        <br /><br />
        4.3 All content you publish only represents your personal views, not the position of the platform. We will not be responsible for user content and behaviors.
        <br /><br />
        4.4 You are fully responsible for the authenticity and legality of the content you publish. If your behavior causes losses to us or a third party, you shall make corresponding compensation and bear legal liabilities.
        <br /><br />
      </div>
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">5. Third-Party Services</h4>
      <div class="item-content">
        5.1 The platform may display third-party advertisements. You agree to view advertisements during use. All transactions based on advertisements shall be at your own risk, and we will not assume relevant liabilities.
        <br /><br />
        5.2 If you use third-party products and services linked by us, you need to abide by the rules of the corresponding platform. We do not guarantee the safety of third-party services and are not responsible for the losses caused thereby.
        <br /><br />
      </div>
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">6. Member Services</h4>
      <div class="item-content">
        6.1 You can purchase member services. The specific rights and prices are subject to the display on the service page.
        <br /><br />
        6.2 We can adjust member prices and service contents at any time.
        <br /><br />
        6.3 After payment, membership cannot be transferred or refunded unless required by law. The membership will expire automatically, and the unused time cannot be postponed.
        <br /><br />
        6.4 The information you submit when applying for membership must be true. Please update your information in time, otherwise you shall bear the consequences of being unable to use member rights normally.
        <br /><br />
      </div>
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">7. Subscription & Virtual Currency</h4>
      <div class="item-content">
        7.1 We provide monthly, bimonthly and quarterly subscription services. Mobile subscriptions will be deducted from your iTunes or Google account. Subscriptions will renew automatically. Please turn off auto-renewal at least 24 hours before expiration if you do not need it. Active subscriptions cannot be refunded halfway. All subscription fees are non-refundable and non-transferable, and cannot be exchanged for cash. We have the right to adjust subscription rules and prices.
        <br /><br />
        7.2 Virtual currency such as diamonds is owned by the platform, and you only have the right to use it for story posting, filters, video chat and other functions. We will not refund or compensate virtual currency due to service adjustment, system failure or function closing. Virtual currency cannot be transferred or cashed. Once the account is closed, all unused virtual currency will be cleared.
        <br /><br />
        7.3 Paid services can be purchased through third-party payment accounts. You need to abide by the rules of the payment platform.
        <br /><br />
        7.4 If you open a subscription through a third party, you need to cancel it on the corresponding platform. After cancellation, you can continue to enjoy rights until the current cycle ends, and no partial refund will be given.
        <br /><br />
        7.5 This agreement is valid during your use of the service. We can terminate your membership at any time without notice and will not refund the remaining fees. Communication fees generated by your use shall be borne by yourself.
        <br /><br />
      </div>
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">8. Data Storage Rules</h4>
      <div class="item-content">
        8.1 We do not guarantee permanent storage of your data and are not liable for data loss or deletion.
        <br /><br />
        8.2 We will set the data storage cycle and capacity by ourselves. It is recommended that you back up important data regularly.
        <br /><br />
        8.3 After you stop using the service, we may permanently delete your data from the server and will not return it.
        <br /><br />
        8.4 You can modify or delete your personal data and published content. Deleting content may cause associated data to disappear, and you shall bear the risk.
        <br /><br />
      </div>
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">9. Risk Tips</h4>
      <div class="item-content">
        9.1 This platform is only an information exchange channel. All operations under your account are your own behaviors, and you shall bear all risks arising from the use of the service.
        <br /><br />
        9.2 If you find other users violating the rules, please contact us in time, and we will deal with it accordingly.
        <br /><br />
        9.3 We can adjust, suspend or close any service functions at any time without prior notice.
        <br /><br />
      </div>
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">10. Intellectual Property</h4>
      <div class="item-content">
        10.1 All intellectual property rights of the platform services belong to us, except the content owned by advertisers.
        <br /><br />
        10.2 The copyright, patent and other intellectual property of the software are all owned by us.
        <br /><br />
        10.3 The software logo, icon, text and product name are protected by trademark and copyright. No one can use or modify them without written permission.
        <br /><br />
        10.4 All intellectual property of the platform and cooperative merchants are protected by law. Unauthorized use and secondary creation are prohibited.
        <br /><br />
        10.5 You own the intellectual property of the content you upload. At the same time, you permanently authorize us and our affiliates to use, edit, disseminate and license your content worldwide for free for any purpose.
        <br /><br />
      </div>
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">11. Liability Regulations</h4>
      <div class="item-content">
        11.2 If you have illegal or breach-of-agreement behaviors, we can issue warnings, restrict functions, ban accounts, blacklist users, deduct virtual assets and take other measures. Virtual assets will not be refunded after account closure.
        <br /><br />
        11.2 We have the right to report your violations to the relevant departments and take legal actions, and you shall bear all consequences.
        <br /><br />
        11.3 You shall compensate for all losses, compensation fees and legal costs caused by your violations.
        <br /><br />
        11.4 You shall compensate us for all losses, lawsuits and expenses caused by your use of the service or violation of the agreement.
        <br /><br />
        11.5 We can choose to use one or multiple disposal methods.
        <br /><br />
      </div>
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">12. Force Majeure & Exemption Clause</h4>
      <div class="item-content">
        12.1 We are not responsible for service interruption caused by natural disasters, wars, policy changes and other uncontrollable factors, but we will try our best to restore the service.
        <br /><br />
        12.2 We do not guarantee that the service will be always safe, stable and error-free, and do not make any implicit or explicit guarantees.
        <br /><br />
        12.3 Network environment, viruses and external software may bring security risks. Please protect your account and data by yourself.
        <br /><br />
        12.4 We are not liable for losses caused by network failure, hacker attacks, system crashes and other technical failures.
        <br /><br />
        12.5 The platform may have false, threatening, illegal and infringing information, and we are not responsible for the losses caused thereby.
        <br /><br />
        12.6 We will carry out regular maintenance and updates. We will not be liable for service suspension with prior notice.
        <br /><br />
        12.7 We have the right but no obligation to find and handle third-party violations in a timely manner.
        <br /><br />
        12.8 We are not responsible for the quality problems of free gifts and free services.
        <br /><br />
        12.9 We will not compensate for indirect losses, profit losses and data losses. The total compensation shall not exceed the total amount you have paid to us.
        <br /><br />
        12.10 Offline communication and transactions between users have risks, and all responsibilities shall be borne by the parties themselves.
        <br /><br />
      </div>
    </div>

    <div class="item-wrap">
      <h4 class="item-heading">13. Additional Rules</h4>
      <div class="item-content">
        13.1 Please read this agreement carefully. Minors must read it under the guidance of guardians.
        <br /><br />
        13.2 All rules and announcements released in the software are supplementary parts of this agreement and have the same legal effect.
        <br /><br />
        13.3 This agreement is governed by the laws of the Hong Kong Special Administrative Region. All disputes shall be submitted to the Hong Kong International Arbitration Centre for arbitration.
        <br /><br />
        13.4 If any clause is deemed invalid, the rest of the clauses still take effect. Both parties will negotiate to replace the invalid clause.
        <br /><br />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      appName: ""
    };
  },
  created() {}
};
</script>

<style scoped>
/* 整体容器：浅底色、无边框卡片风格，和之前样式完全区分 */
.policy-container {
  max-width: 980px;
  margin: 40px auto;
  padding: 30px;
  background-color: #f7f9fc;
  border-radius: 8px;
  font-family: Arial, Helvetica, sans-serif;
}

/* 主标题 */
.main-title {
  font-size: 28px;
  font-weight: bold;
  text-align: center;
  color: #1a202c;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid #cbd5e0;
}

/* 开篇介绍 */
.intro-block {
  font-size: 16px;
  line-height: 1.75;
  color: #2d3748;
  margin-bottom: 30px;
  text-align: left;
}

/* 每个章节外层容器 */
.item-wrap {
  background: #ffffff;
  padding: 18px 22px;
  margin-bottom: 20px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

/* 章节小标题 */
.item-heading {
  font-size: 17px;
  color: #2563eb;
  margin: 0 0 10px 0;
  font-weight: 600;
}

/* 正文内容 */
.item-content {
  font-size: 15px;
  line-height: 1.7;
  color: #475569;
  text-align: left;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .policy-container {
    margin: 15px;
    padding: 20px 15px;
  }
  .main-title {
    font-size: 24px;
  }
  .intro-block,
  .item-content {
    font-size: 14px;
  }
  .item-heading {
    font-size: 16px;
  }
  .item-wrap {
    padding: 15px;
  }
}
</style>